import React from "react";
import { Outlet, Route } from "react-router-dom";
import Warmup from "@/components/Warmup";
import useAuth from "../hooks/useAuth";
import ToastAlertProvider from "@/providers/ToastAlertProvider";
import ThemeProvider from "@/providers/ThemeProvider";
import MainLayout from "@/pages/Shared/MainLayout";
import { AIConfigsProvider } from "@/providers/AIConfigsProvider";
import { AutomationsProvider } from "@/providers/AutomationsProvider";
import { RoleListItemsProvider } from "@/providers/RoleListItemsProvider";
import { RulesProvider } from "@/providers/RulesProvider";
import { SubscriptionsProvider } from "@/providers/SubscriptionsProvider";
import { SelectedTeamProvider } from "@/providers/SelectedTeamProvider";
import LanguageConfigsProvider from "@/pages/Messaging/WebMessenger/providers/LanguageConfigProvider";
import StyleConfigsProvider from "@/pages/Messaging/WebMessenger/providers/StyleConfigProvider";
import { SkillListItemsProvider } from "@/providers/SkillsProvider";
import { TeamsProvider } from "@/providers/TeamsProvider";
import { UsersProvider } from "@/providers/UsersProvider";
import ProfileProvider from "@/providers/ProfileProvider";
import StatusProvider from "@/providers/StatusProvider";
import { SiteProvider } from "@/providers/SiteProvider";
import { WorkflowsProvider } from "@/providers/WorkflowsProvider";
import { ConditionPresetsProvider } from "@/providers/ConditionPresetsProvider";
import { SurveysProvider } from "@/providers/SurveysProvider";
import { SharedSchedulesProvider } from "@/providers/SharedSchedulesProvider";
import { AzureIndexesProvider } from "@/providers/AzureIndexesProvider";
import ChatWindow from "@/pages/Messaging/ChatWindow";
import { ChatWindowsProvider } from "@/providers/ChatWindowsProvider";
import { PrimaryButton, SecondaryButton } from "@/components/ds";
import {
  ENTRA_REFRESH_TIMEOUT_MS,
  AUTH_CONFIG_FETCH_TIMEOUT_MS,
} from "@/services/AuthService";

// Absolute wall-clock ceiling on the unauthenticated state. Deliberately a
// TIMER and not a .catch(): the worst failure modes here STALL rather than
// reject (a fetch with no timeout, an MSAL redirect that never navigates, a
// silent `return` inside entraLoginRedirect). A .catch() never fires for any of
// those, which is exactly how this screen could sit on the branded loading
// spinner forever with zero console output. Incident 2026-08-28.
//
// DERIVED, never a literal — a bare number here would reintroduce the exact
// "two independent timeouts silently drift out of order" bug this file's fix
// exists to eliminate. It must comfortably exceed everything that can run
// before a redirect actually navigates: the silent refresh ceiling, the
// /auth/config lookup, then msal.initialize() + handleRedirectPromise() + the
// navigation itself. The generous slack is deliberate — useWarmup.ts notes a
// cold post-deploy server can take 10-15s — because firing this early shows a
// scary error to someone who was simply about to be redirected. Pinned by a
// source-scan test in AuthService.cold-load-race.test.ts.
const AUTH_WALL_CLOCK_TIMEOUT_MS =
  ENTRA_REFRESH_TIMEOUT_MS + AUTH_CONFIG_FETCH_TIMEOUT_MS + 20000;

export function PrivateRoute(props: any) {
  const { component, ...rest } = props;
  const auth = useAuth();
  const [authenticated, setAuthenticated] = React.useState(auth.isAuthenticated());
  const [authFailed, setAuthFailed] = React.useState(false);
  // Prevent loginRedirect() from firing more than once per component lifetime,
  // even if PrivateRoute remounts rapidly during an MSAL auth-code exchange.
  const redirectFired = React.useRef(false);

  React.useEffect(() => {
    if (auth.isAuthenticated()) {
      setAuthenticated(true);
      return;
    }
    if (redirectFired.current) return;

    let settled = false;

    // Independent of the promise below — see AUTH_WALL_CLOCK_TIMEOUT_MS.
    const ceiling = setTimeout(() => {
      if (settled) return;
      settled = true;
      // A late-arriving token still wins over showing an error.
      if (auth.isAuthenticated()) {
        setAuthenticated(true);
        return;
      }
      console.warn(
        `[Auth] Sign-in did not complete within ${AUTH_WALL_CLOCK_TIMEOUT_MS}ms — showing recovery UI instead of hanging.`
      );
      setAuthFailed(true);
    }, AUTH_WALL_CLOCK_TIMEOUT_MS);

    // Try silent refresh before redirecting to login
    auth
      .entraTokenRefresh()
      .then((ok) => {
        if (settled) return;

        // Re-check rather than trusting `ok` alone: another path (the
        // module-load checkAccessToken, an MSAL redirect completing) may have
        // populated a valid token while this refresh was resolving. Redirecting
        // an already-authenticated user to login is a needless round-trip that
        // also discards the current URL's query string.
        if (ok || auth.isAuthenticated()) {
          settled = true;
          clearTimeout(ceiling);
          setAuthenticated(true);
          return;
        }

        if (!redirectFired.current) {
          redirectFired.current = true;
          // Leave `settled` false and the ceiling armed: loginRedirect() is
          // expected to navigate away. If it does not, the ceiling fires and
          // the user gets a real error instead of an endless spinner.
          auth.loginRedirect();
        }
      })
      .catch((err: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(ceiling);
        console.error("[Auth] Silent refresh failed in PrivateRoute:", err?.message ?? err);
        setAuthFailed(true);
      });

    return () => clearTimeout(ceiling);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authenticated) {
    return (
      <ThemeProvider>
      <SiteProvider>
        <TeamsProvider>
          <ProfileProvider>
            <StatusProvider>
              <UsersProvider>
                <SkillListItemsProvider>
                  <SelectedTeamProvider>
                    <RoleListItemsProvider>
                      <ToastAlertProvider>
                        <RulesProvider>
                          <SubscriptionsProvider>
                          <AutomationsProvider>
                            <WorkflowsProvider>
                              <ConditionPresetsProvider>
                              <AIConfigsProvider>
                                <AzureIndexesProvider>
                                  <SurveysProvider>
                                    <SharedSchedulesProvider>
                                      <ChatWindowsProvider>
                                        <LanguageConfigsProvider>
                                          <StyleConfigsProvider>
                                            <MainLayout>
                                              <Outlet />
                                            </MainLayout>
                                          </StyleConfigsProvider>
                                        </LanguageConfigsProvider>
                                      </ChatWindowsProvider>
                                    </SharedSchedulesProvider>
                                  </SurveysProvider>
                                </AzureIndexesProvider>
                              </AIConfigsProvider>
                              </ConditionPresetsProvider>
                            </WorkflowsProvider>
                          </AutomationsProvider>
                          </SubscriptionsProvider>
                        </RulesProvider>
                      </ToastAlertProvider>
                    </RoleListItemsProvider>
                  </SelectedTeamProvider>
                </SkillListItemsProvider>
              </UsersProvider>
            </StatusProvider>
          </ProfileProvider>
        </TeamsProvider>
      </SiteProvider>
      </ThemeProvider>
    );
  }

  // Sign-in could not complete. Never leave the user on an indefinite loading
  // screen with no explanation and no way out.
  if (authFailed) {
    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 text-center dark:bg-zinc-900"
      >
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          We couldn&rsquo;t finish signing you in
        </h1>
        <p className="max-w-md text-sm text-zinc-600 dark:text-zinc-300">
          Your session could not be verified. This is usually temporary — trying
          again normally resolves it.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <PrimaryButton onClick={() => window.location.reload()}>
            Try again
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              redirectFired.current = true;
              auth.loginRedirect();
            }}
          >
            Sign in again
          </SecondaryButton>
        </div>
      </div>
    );
  }

  // Branded warmup screen (logo lockup) — never a bare spinner.
  return <Warmup phase="loading" />;
}
